import React, { useEffect, useState } from 'react';

import DatePicker from 'react-datepicker';
import { observer } from 'mobx-react';
import { Form, Col } from 'react-bootstrap';
import { Field } from 'react-final-form';
import { logger } from '@qlean/front-logger';
import moment from 'moment';

import MetaStore from 'src/store/MetaStore';
import { FieldLabel } from 'src/components/Fields/FieldLabel';
import { FieldError } from 'src/components/Fields/FieldError';
import AvailableSlotsButton from 'src/components/AvailableSlots/AvailableSlotsButton';

import { TFieldProp } from 'src/components/Fields/field.interface';

import ru from 'date-fns/locale/ru';

import 'src/vendor/libs/react-datepicker/react-datepicker.scss';
import { ERROR_MESSAGES, isValidStartDate } from 'src/utils/validation';
import usePrevious from 'src/utils/usePrevious';
import { lmsServiceTypes } from '../../../utils/dictionary';
import { isSameDay } from 'date-fns';

export interface ITimeSlotField {
  prospectId: string;
  serviceType: string;
  timeslotsSource: 'CRM' | 'LMS' | null;
}

export interface ITimeSlotValue {
  date?: string;
  startDate?: string;
}
interface IValueDate {
  date?: Date;
  startDate?: Date;
}

export type TTimeSlotField = ITimeSlotField & TFieldProp<ITimeSlotValue>;
const MOSCOW_TIMEZONE_OFFSET = 180;

// DatePicker reads local Date components; keep the region's clock time in those components.
const toPickerDate = (value: moment.Moment): Date => new Date(
  value.year(), value.month(), value.date(), value.hour(), value.minute(), value.second(), value.millisecond(),
);

const getValueDate = (value?: ITimeSlotValue): IValueDate => {
  const startDate = value?.startDate ? moment(value.startDate).utcOffset(MOSCOW_TIMEZONE_OFFSET) : undefined;
  const dateString = startDate ? startDate.format('YYYY-MM-DD') : value?.date;
  return {
    date: dateString ? moment(dateString, 'YYYY-MM-DD').startOf('day').hour(12).toDate() : undefined,
    startDate: startDate ? toPickerDate(startDate) : undefined,
  };
};

const isDateEqual = (a?: Date, b?: Date) => {
  return a?.getTime() === b?.getTime();
};

const isValuesDateEqual = (a: IValueDate, b: IValueDate): boolean => {
  return isDateEqual(a?.date, b?.date) && isDateEqual(a?.startDate, b?.startDate);
};

export const TimeSlotField = observer((props: TTimeSlotField) => {
  const { name, isRequired, isReadonly, prospectId, serviceType, errors, timeslotsSource } = props;
  const initialValue = getValueDate(props.initialValue);
  const prevInitialValue = getValueDate(usePrevious(props.initialValue));

  const { getAvailableDates, getAvailableTimeSlots, getAvailableLmsTimeSlotsByCoordinates } = MetaStore;

  const [includedDates, setIncludedDates] = useState<Date[] | undefined>(undefined);
  const [includedTimes, setIncludedTimes] = useState<Date[] | undefined>([]);
  const [chosedDate, setChosedDate] = useState<Date | undefined>(undefined);
  const [chosedTime, setChosedTime] = useState<Date | undefined>(undefined);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    setShowError(Boolean(errors));
  }, [errors]);

  const retrieveDates = async (): Promise<string[] | undefined> => {
    if (!prospectId || !serviceType || !timeslotsSource) {
      return undefined;
    }

    const requestBody = {
      prospectId,
    };

    if (timeslotsSource === 'CRM' && !lmsServiceTypes.includes(serviceType)) {
      logger.debug('fetchDates', prospectId);
      return getAvailableDates(requestBody);
    }

    if (timeslotsSource === 'LMS') {
      logger.debug('fetchDates LMS', prospectId);
      return getAvailableLmsTimeSlotsByCoordinates(requestBody);
    }

    return [];
  };

  const fetchDates = async () => {
    const shouldResetDates = timeslotsSource === null;
    setShowError(false);

    const availableDates: string[] | undefined = await retrieveDates();
    logger.debug('fetchDates result', prospectId);

    if (!availableDates) {
      setIncludedDates(undefined);
    } else {
      setIncludedDates(availableDates.map((date) => toPickerDate(moment(date).utcOffset(MOSCOW_TIMEZONE_OFFSET))));
    }

    if (shouldResetDates) {
      setIncludedTimes(undefined);
    }
  };

  const retrieveTimeSlots = async (): Promise<string[]> => {
    if (!serviceType || !prospectId || !chosedDate) {
      return [];
    }

    const requestBody = {
      prospectId,
      date: chosedDate.toUTCString(),
    };

    if (timeslotsSource === 'CRM') {
      return getAvailableTimeSlots(requestBody);
    }

    if (timeslotsSource === 'LMS') {
      return getAvailableLmsTimeSlotsByCoordinates(requestBody);
    }

    return [];
  };

  const fetchTimeSlots = async () => {
    const shouldResetSlots = timeslotsSource === null;
    setShowError(false);

    const availableTimes: string[] = await retrieveTimeSlots();
    let dates = availableTimes.map((date) => toPickerDate(moment(date).utcOffset(MOSCOW_TIMEZONE_OFFSET)));

    if (shouldResetSlots) {
      setIncludedTimes(undefined);
    }

    if (!initialValue?.startDate || !chosedDate || !isSameDay(chosedDate, initialValue.startDate)) {
      setChosedTime(undefined);
    }

    if (chosedDate) {
      dates = dates.filter((d) => isSameDay(d, chosedDate));
    }
    setIncludedTimes(dates.length ? dates : undefined);
  };

  const onBlur = () => {
    setShowError(!isValidStartDate(chosedTime?.toISOString()));
  };

  return (
    <Field
      name={name}
      render={({ input }) => {
        useEffect(() => {
          if (!isValuesDateEqual(initialValue, prevInitialValue) && !isDateEqual(chosedDate, initialValue.date)) {
            setChosedDate(initialValue.date);
            setChosedTime(initialValue.startDate);
            input.onChange({
              date: initialValue.date ? moment(initialValue.date).format('YYYY-MM-DD') : null,
              startDate: props.initialValue?.startDate ? new Date(props.initialValue.startDate) : undefined,
            });
          }
        }, [props.initialValue]);
        return (
          <Form.Row>
            <Form.Group className="mb-0" controlId={name} as={Col} md={6}>
              <FieldLabel label="Дата" isRequired={isRequired} />
              <DatePicker
                className={`form-control ${showError ? 'is-invalid' : ''}`}
                selected={chosedDate}
                dateFormat="dd MMMM yyyy"
                locale={ru}
                placeholderText="Выберите дату"
                disabled={isReadonly}
                onFocus={fetchDates}
                onBlur={onBlur}
                includeDates={includedDates}
                filterDate={(date) => {
                  return date >= new Date().setDate(new Date().getDate() - 1);
                }}
                onChange={(date) => {
                  if (date && !isNaN(date.getTime())) {
                    const dateMidDay = new Date(date?.setHours(12, 0));
                    const dateMidDayString = moment(dateMidDay).format('YYYY-MM-DD');
                    setChosedDate(dateMidDay);
                    input.onChange({
                      date: dateMidDayString,
                      startDate: null,
                    });
                  } else {
                    input.onChange({ date: null, startDate: null });
                    setChosedDate(undefined);
                  }
                  setChosedTime(undefined);
                  onBlur();
                }}
              />
            </Form.Group>
            <Form.Group className="mb-0" controlId={name} as={Col} md={6}>
              <FieldLabel label="Время" isRequired={isRequired} />
              <DatePicker
                className={`form-control ${showError ? 'is-invalid' : ''}`}
                selected={chosedTime}
                onChange={(date: Date) => {
                  if (chosedDate && date && !isNaN(date.getTime())) {
                    const startDate = moment(chosedDate)
                      .hour(date.getHours()).minute(date.getMinutes()).second(0).millisecond(0)
                      .utcOffset(MOSCOW_TIMEZONE_OFFSET, true);
                    input.onChange({
                      date: moment(chosedDate).format('YYYY-MM-DD'),
                      startDate: startDate.toDate(),
                    });
                    setChosedTime(toPickerDate(startDate));
                  }
                }}
                disabled={isReadonly || !chosedDate}
                onFocus={fetchTimeSlots}
                onBlur={onBlur}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={30}
                locale={ru}
                placeholderText="Выберите время"
                dateFormat="HH:mm"
                timeFormat="HH:mm"
                timeCaption="Время"
                includeTimes={includedTimes}
              />
            </Form.Group>
            <div className="mx-1">
              <Form.Control readOnly={true} hidden={true} value={chosedTime?.toISOString() || ''} isInvalid={showError} />
              <FieldError error={errors ? `${errors}` : ERROR_MESSAGES.START_DATE} />
            </div>
            <Col xs={12}>
              <AvailableSlotsButton
                serviceType={serviceType}
                onSelect={isReadonly ? undefined : (startAt) => {
                  const startDate = moment.parseZone(startAt);
                  const date = startDate.format('YYYY-MM-DD');
                  setChosedDate(moment(date).hour(12).toDate());
                  setChosedTime(toPickerDate(startDate));
                  setShowError(false);
                  input.onChange({ date, startDate: startDate.toDate() });
                }}
              />
            </Col>
          </Form.Row>
        );
      }}
    />
  );
});
